import React from 'react';
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon, 
  PhoneIcon, 
  EllipsisVerticalIcon 
} from '@heroicons/react/24/outline';
import { 
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatarPreco } from '../../utils/priceUtils';
import AppointmentCard from '../ui/AppointmentCard';
import { useWindowSize } from '../../hooks';

const AppointmentSearchResults = ({ 
  appointments, 
  onView, 
  onEdit, 
  onReschedule, 
  onDelete, 
  onConfirm, 
  onCancel,
  isLoading
}) => {
  const { width } = useWindowSize();
  const isMobile = width < 768; // Considera dispositivos com menos de 768px como mobile
  
  // Função para formatar a data como string
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Data não disponível';
    
    try {
      if (typeof dateStr === 'string') {
        if (dateStr.includes('-')) {
          // Formato ISO (YYYY-MM-DD)
          const [year, month, day] = dateStr.split('-');
          return `${day}/${month}/${year}`;
        } else if (dateStr.includes('/')) {
          // Já está no formato brasileiro
          return dateStr;
        }
      } else if (dateStr instanceof Date) {
        return format(dateStr, 'dd/MM/yyyy', { locale: ptBR });
      }
      
      return dateStr;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  // Função para formatar o telefone
  const formatPhone = (phone) => {
    if (!phone) return '';
    
    // Remove todos os caracteres não numéricos
    const cleaned = phone.replace(/\D/g, '');
    
    // Formato: (XX) XXXXX-XXXX
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    } 
    // Formato: (XX) XXXX-XXXX
    else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    
    // Retorna o valor original se não conseguir formatar
    return phone;
  };

  // Função para obter o status formatado
  const getStatus = (appointment) => {
    if (appointment.cancelado) {
      return { label: 'Cancelado', color: 'bg-red-100 text-red-800' };
    } else if (appointment.confirmado) {
      return { label: 'Confirmado', color: 'bg-green-100 text-green-800' };
    } else {
      return { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' };
    }
  };

  // Função para obter o nome do serviço principal
  const getMainService = (appointment) => {
    if (!appointment.servicos || !Array.isArray(appointment.servicos) || appointment.servicos.length === 0) {
      return 'Serviço não especificado';
    }
    
    const firstService = appointment.servicos[0];
    return typeof firstService === 'object' 
      ? firstService.nome 
      : 'Serviço não detalhado';
  };

  // Função para obter o valor total do agendamento
  const getTotalValue = (appointment) => {
    if (appointment.valorTotal) {
      return formatarPreco(appointment.valorTotal);
    }
    
    // Caso não tenha o valor total já calculado
    let total = 0;
    
    if (Array.isArray(appointment.servicos)) {
      appointment.servicos.forEach(servico => {
        if (typeof servico === 'object' && servico.valor) {
          total += parseFloat(servico.valor);
        } else if (typeof servico === 'object' && servico.preco) {
          total += parseFloat(servico.preco);
        }
      });
    }
    
    return total > 0 ? formatarPreco(total) : 'Valor não disponível';
  };

  // Função para obter o nome do profissional
  const getProfessionalName = (appointment) => {
    if (!appointment.profissional) return 'Não atribuído';
    
    return typeof appointment.profissional === 'object'
      ? appointment.profissional.nome
      : 'Profissional ' + appointment.profissional;
  };

  // Skeleton loader para carregamento
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // Mensagem para quando não há resultados
  if (!appointments || appointments.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4 text-center text-gray-500">
        <p>Nenhum agendamento encontrado com os filtros atuais.</p>
      </div>
    );
  }

  // Função para transformar o appointment no formato esperado pelo AppointmentCard
  const formatAppointmentForCard = (appointment) => {
    const status = appointment.cancelado 
      ? 'canceled'
      : appointment.confirmado 
        ? 'completed' 
        : 'no-show';

    return {
      id: appointment.id,
      date: new Date(appointment.data),
      time: appointment.horario,
      clientName: appointment.cliente || 'Cliente não identificado',
      serviceName: getMainService(appointment),
      professionalName: getProfessionalName(appointment),
      status,
      value: appointment.valorTotal || 0,
      duration: appointment.duracaoTotal || 30,
      isPaid: !!appointment.pago,
      paymentMethod: appointment.metodoPagamento || 'Não informado',
      // Dados adicionais para operações
      originalAppointment: appointment
    };
  };

  // Renderização unificada usando card para ambos mobile e desktop
  return (
    <div className="space-y-4">
      {/* Grid de cards - adaptável entre mobile (1 coluna) e desktop (2 ou 3 colunas) */}
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'} gap-4`}>
        {appointments.map((appointment) => {
          const formattedAppointment = formatAppointmentForCard(appointment);
          
          return (
            <div key={appointment.id} className="relative">
              <div 
                onClick={() => onView && onView(appointment)}
                className="cursor-pointer"
              >
                <AppointmentCard 
                  appointment={formattedAppointment}
                  isMobile={isMobile}
                  showDetails={true}
                />
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Paginação - pode ser implementada aqui se necessário */}
      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div className="text-sm text-gray-700">
          Exibindo <span className="font-medium">{appointments.length}</span> agendamento(s)
        </div>
      </div>
    </div>
  );
};

export default AppointmentSearchResults; 